import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Grid, makeStyles } from '@material-ui/core';
import { I18n } from 'src/translations/i18n';
import { duration as MUITransitionDuration } from '@material-ui/core/styles/transitions';
import { EMPTY_ARRAY, stopEventBubble } from 'src/helpers/FPUtils';
import { selectLocale } from 'src/store/selectors/translationSelectors';
import { excludeOptionAndChildrenIds, getFilteredOptions, getOptionAndChildrenIds, getOptionsQty } from 'src/components/Inputs/instruments';
import SelectAllElement from 'src/components/Inputs/DropDownSelector/SelectAllElement';
import { getFlatOptionsList, getOptionById, SEARCH_INPUT_ID, SELECT_ELEMENT_HEIGHT } from 'src/components/Inputs/DropDownSelector/instruments';
import { List } from 'react-virtualized';
import { isNil, uniq } from 'ramda';
import Utils from 'src/utils';
import classNames from 'classnames';
import FormControlLabel from '../../Form/FormControlLabel';
import TextInput from '../TextInput';
import SelectElement from './SelectElement';
import DDButton from './DDButton';
const SEARCH_INPUT_HEIGHT = 46;
const useStyles = makeStyles((theme) => ({
    searchInputContainer: {
        position: 'absolute',
        left: 0,
        right: 5,
        top: 0,
        zIndex: 1,
        backgroundColor: theme.palette.ddSelector.background
    },
    searchInput: {
        margin: 6
    },
    selectAllButton: {
        '&:hover': {
            background: theme.palette.ddSelector.itemHover
        }
    },
    errorField: {
        '& label': {
            color: '#a94442'
        },
        '& button': {
            borderColor: '#a94442'
        }
    },
    errorText: {
        color: '#a94442'
    },
    noOptions: {
        padding: '10px 0',
        textAlign: 'center'
    },
    listWrapper: ({ showFilter }) => ({
        paddingTop: showFilter ? SEARCH_INPUT_HEIGHT : 0
    }),
    virtualizedListWrapper: ({ showFilter }) => ({
        overflowY: 'scroll !important',
        paddingTop: showFilter ? SEARCH_INPUT_HEIGHT : 0,
        ...theme.themedClasses.scrollbar
    }),
    readonlyList: {
        pointerEvents: 'none'
    }
}));
function DropDownSelector({ options, values: rawValues, onSelect, isSingleSelect, filterEnabled, disabled, containerClass = '', className = '', buttonWidth = 180, listWidth = 350, maxHeight = 440, label = '', mouseInKey = '', id = '', dropDownId = 'dropdown-size-large', isRequired, errorText = '', isValid = true, placeholder = '', readonly = false, deselectable, unselectMissingOnChange, selectElementRenderer }) {
    const [search, setSearch] = useState('');
    const locale = useSelector(selectLocale);
    const totalLength = useMemo(() => getOptionsQty(options), [options]);
    const virtualizedList = totalLength > 50;
    const showFilter = useMemo(() => filterEnabled && !readonly, [filterEnabled, readonly]);
    const showSelectAllButton = useMemo(() => !isSingleSelect && !readonly, [isSingleSelect, readonly]);
    const values = useMemo(() => {
        return rawValues.filter((value) => !isNil(value));
    }, [rawValues]);
    const allOptionsMap = useMemo(() => {
        return Utils.arrays.arrayToMap('value', options);
    }, [options]);
    const handleClick = useCallback(({ value }, event) => {
        const isDeselect = values.indexOf(value) >= 0;
        let newValues;
        if (isSingleSelect) {
            document.dispatchEvent(new MouseEvent('click'));
            newValues = deselectable && isDeselect ? EMPTY_ARRAY : [value];
        }
        else {
            const intoDeep = event.shiftKey;
            if (intoDeep) {
                newValues = isDeselect
                    ? excludeOptionAndChildrenIds(values, value, options)
                    : [...values, ...getOptionAndChildrenIds(value, options)];
            }
            else {
                newValues = isDeselect
                    ? values.filter((val) => val !== value)
                    : [...values, value];
            }
        }
        newValues = unselectMissingOnChange
            ? newValues.filter((val) => allOptionsMap.has(val))
            : newValues;
        onSelect(newValues, value);
    }, [values, isSingleSelect, unselectMissingOnChange, onSelect, deselectable, options, allOptionsMap]);
    const title = useMemo(() => {
        const selected = values.length;
        if (selected === 0) {
            if (options.length === 0)
                return I18n.t('NoOptions');
            return I18n.t(placeholder || 'NotSelected');
        }
        else if (selected === 1) {
            const selectedOption = getOptionById(options, values[0]);
            return selectedOption ? selectedOption.label : `1 ${I18n.t('Selected')}`;
        }
        else if (selected === totalLength) {
            return I18n.t('AllSelected');
        }
        else {
            return `${selected} ${I18n.t('Selected')}`;
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, options, totalLength, locale]);
    const filteredOptions = useMemo(() => (readonly
        ? options.filter((option) => values.includes(option.value))
        : getFilteredOptions(options, search)), [readonly, options, search, values]);
    const flatOptionsList = useMemo(() => (getFlatOptionsList(filteredOptions)), [filteredOptions]);
    useEffect(() => {
        if (!options.length)
            return;
        const flatOptAll = getFlatOptionsList(options);
        const flatOptionsMap = Utils.arrays.arrayToMap('value', flatOptAll);
        const existingValues = values.filter((value) => flatOptionsMap.has(value));
        if (existingValues.length < values.length) {
            onSelect(existingValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options]);
    const valuesMap = useMemo(() => {
        const map = new Map();
        values.forEach((value) => map.set(value, true));
        return map;
    }, [values]);
    const isAllFilteredSelected = useMemo(() => {
        return flatOptionsList.every((option) => valuesMap.has(option.value));
    }, [flatOptionsList, valuesMap]);
    const handleSelectOrDeselectAll = useCallback((e) => {
        stopEventBubble(e);
        if (isAllFilteredSelected) {
            const selectedOptionsMap = Utils.arrays.arrayToMap('value', flatOptionsList);
            onSelect(values.filter((value) => !selectedOptionsMap.has(value)));
        }
        else {
            onSelect(uniq(values.concat(flatOptionsList.map((option) => option.value))));
        }
    }, [isAllFilteredSelected, flatOptionsList, onSelect, values]);
    const closeCallback = useCallback(() => {
        setTimeout(() => setSearch(''), MUITransitionDuration.standard);
    }, [setSearch]);
    const classes = useStyles({ showFilter });
    const renderFilterInput = useCallback(() => showFilter && (React.createElement(TextInput, { id: SEARCH_INPUT_ID, value: search, placeholder: I18n.t('Search'), onChange: setSearch, autoFocus: true, className: classes.searchInput, disableValidation: true, containerClassName: classes.searchInputContainer, debounce: 300, autoComplete: "off" })), [classes, search, showFilter]);
    const getSelectAllButton = useCallback((style = {}) => (React.createElement(SelectAllElement, { key: "selectAll", onClick: handleSelectOrDeselectAll, isAllSelected: isAllFilteredSelected, style: style })), [handleSelectOrDeselectAll, isAllFilteredSelected]);
    const getSelectElement = useCallback((option, style = {}) => {
        const props = {
            key: option.value,
            option,
            handleClick,
            isSelected: values.indexOf(option.value) >= 0,
            isSingleSelect,
            nestingLevel: option.nestingLevel,
            style,
            disabled: readonly
        };
        if (selectElementRenderer) {
            return selectElementRenderer(props);
        }
        else {
            return React.createElement(SelectElement, { ...props });
        }
    }, [selectElementRenderer, handleClick, isSingleSelect, readonly, values]);
    const optionRenderer = useCallback(({ index, style }) => {
        if (!flatOptionsList?.length && showSelectAllButton)
            return getSelectAllButton();
        if (index === 0 && showSelectAllButton)
            return getSelectAllButton(style);
        const optIdx = isSingleSelect || readonly ? index : index - 1;
        return getSelectElement(flatOptionsList[optIdx], style);
    }, [flatOptionsList, getSelectAllButton, getSelectElement, isSingleSelect, readonly, showSelectAllButton]);
    const ddDisabled = disabled || !options.length || (readonly && values.length <= 1);
    const rowCount = flatOptionsList.length + (showSelectAllButton ? 1 : 0);
    const popupContentHeight = rowCount * SELECT_ELEMENT_HEIGHT + (showFilter ? SEARCH_INPUT_HEIGHT : 0);
    return (React.createElement(Grid, { item: true, xs: "auto", className: `form-input column-input ${containerClass} ${!isValid ? classes.errorField : ''}`, id: id },
        (label || (!isValid && errorText)) && (React.createElement(Grid, { container: true, spacing: 2, justifyContent: "space-between", wrap: "nowrap" },
            label && (React.createElement(Grid, { item: true },
                React.createElement(FormControlLabel, { title: I18n.t(label), mouseInKey: mouseInKey, isRequired: isRequired }))),
            !isValid && errorText && (React.createElement(Grid, { item: true },
                React.createElement("span", { className: classes.errorText }, errorText))))),
        React.createElement(DDButton, { className: className, disabled: ddDisabled, buttonWidth: buttonWidth, listWidth: listWidth, maxHeight: maxHeight, dropDownId: dropDownId, closeCallback: closeCallback, title: title, overflowY: virtualizedList ? 'hidden' : 'scroll' },
            React.createElement("div", { className: classNames(readonly && classes.readonlyList) },
                renderFilterInput(),
                virtualizedList
                    ? (React.createElement(List, { className: classes.virtualizedListWrapper, width: listWidth, height: Math.min(popupContentHeight, maxHeight), rowCount: rowCount, rowHeight: SELECT_ELEMENT_HEIGHT, rowRenderer: optionRenderer })) : (React.createElement("div", { className: classes.listWrapper },
                    showSelectAllButton && getSelectAllButton(),
                    flatOptionsList.map((option) => getSelectElement(option))))))));
}
export default DropDownSelector;
